import { cn } from '@src/design-system/helpers';
import { PhoneNumberInput } from '@src/design-system/phone-number-input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import {
  createContext,
  useContext,
  useState,
  type ComponentProps,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { Value } from 'react-phone-number-input';
import { EditableCell } from './editable-cell';
import { useEditableCellClickHandlers } from '../hooks/use-editable-cell-click-handlers';
import { copyToClipboard } from '../utils';

const EditablePhoneNumberCellContext = createContext<{
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}>({
  isOpen: false,
  setIsOpen: () => {},
});

export function EditablePhoneNumberCell({
  copyToText,
  ...props
}: ComponentProps<'div'> & { copyToText?: string }) {
  const [isOpen, setIsOpen] = useState(false);

  const { handleClick, handleDoubleClick } = useEditableCellClickHandlers();

  return (
    <EditablePhoneNumberCellContext.Provider value={{ isOpen, setIsOpen }}>
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <EditableCell
          onClick={() => {
            if (!copyToText) return;
            handleClick(() => copyToClipboard(copyToText));
          }}
          onDoubleClick={() => handleDoubleClick(() => setIsOpen(true))}
          {...props}
        />
      </Popover>
    </EditablePhoneNumberCellContext.Provider>
  );
}

export function EditablePhoneNumberCellEditMode({
  value,
  onChange,
  onBlur,
  ...props
}: Omit<ComponentProps<typeof PhoneNumberInput>, 'onChange'> & {
  value: string;
  onChange?: (value: Value) => void;
  onBlur?: (value: string) => void;
}) {
  const { setIsOpen } = useContext(EditablePhoneNumberCellContext);
  const [inputValue, setInputValue] = useState(value);

  return (
    <PopoverContent
      align='start'
      alignOffset={-8}
      className='p-0 border-none bg-transparent rounded-lg'
    >
      <PhoneNumberInput
        className='text-sm'
        onChange={(value) => {
          setInputValue(value || '');
          onChange?.(value as Value);
        }}
        onBlur={() => {
          setIsOpen(false);
          onBlur?.(inputValue);
        }}
        value={inputValue}
        {...props}
      />
    </PopoverContent>
  );
}

export function EditablePhoneNumberCellViewMode({
  className,
  onClick,
  ...props
}: ComponentProps<typeof PopoverTrigger>) {
  return (
    <PopoverTrigger
      className={cn(
        'truncate items-start text-start w-full h-full cursor-pointer',
        className
      )}
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
      {...props}
    />
  );
}
